import { useCallback, useEffect, useId, useMemo, type ReactNode } from "react"
import Split from "react-split"

import type { RerunMetricsById } from "@/metrics/rerun"
import {
  buildMetricAutoAddRuleSources,
  buildMetricFilterAdapters,
  buildMetricRenderAdapters,
} from "@/metrics/runtime-adapters"
import type { CompareBy } from "@/run-detail/compare-by"
import {
  Checkbox,
  cn,
  createResizeGrip,
  Label,
  TABLE_FRAME_CLASS,
  TableSkeleton,
} from "@coval/ui"
import { X } from "lucide-react"

import type { MetricViews } from "@/types/MetricViewConfig"
import type { ComparisonPlotConfig } from "@/utils/comparisonPlot"
import {
  removePlotFromLayout,
  updatePlotInLayout,
  type MetricLayoutConfig,
} from "@/utils/metricLayout"

import {
  GroupedResultsTable,
  RunMetricCards,
  RunResultsTable,
  ScopedSimulationsBanner,
} from "../../results"
import {
  COMPARISON_PLOT_ENTRIES,
  PlotCard,
  PlotOptionsPanel,
} from "../comparison"
import {
  DEFAULT_REPORT_PANE_VISIBILITY,
  type ReportPaneVisibility,
} from "../utils/reportViewConfig"
import { REPORT_VIEW_MODE, type ReportViewMode } from "../utils/reportViewMode"
import type { ReportRunsById } from "../utils/reportWorkspaceData"

type ReportRunRows = Parameters<typeof RunMetricCards>[0]["runRows"]
type MetricRecordsById = Parameters<typeof buildMetricRenderAdapters>[0]
type AgentsById = NonNullable<
  Parameters<typeof RunResultsTable>[0]["agentsById"]
>
type PersonasById = NonNullable<
  Parameters<typeof RunResultsTable>[0]["personasById"]
>

interface ReportWorkspaceProps {
  agentsById: AgentsById
  compareByColors: Record<string, string> | null
  compareByConfigForGroup: CompareBy | null
  compareByDisplayLabels: Record<string, string>
  hoveredRunId?: string | null
  isLoading: boolean
  isShared?: boolean
  leftPaneHeader?: ReactNode
  /** When provided, replaces the metric-cards left pane entirely. Used by custom-dimension selection mode. */
  leftPaneOverride?: ReactNode
  metricRecordsById: MetricRecordsById
  /** When provided, the metric drill-in view shows a Rerun Metric button. */
  rerunMetricRecordsById?: RerunMetricsById | null
  metricViews?: MetricViews
  /** When true, forces the grouped table into select mode and surfaces the toolbar. */
  forceSelectMode?: boolean
  /** Extra toolbar content rendered in the grouped table's selection toolbar (e.g. Assign-to-group buttons). */
  selectionToolbarLeading?: ReactNode
  /** Per-row group chip data (only rendered when selection mode is on). */
  customDimensionGroupBySimId?: Record<string, string>
  customDimensionGroupNameById?: Record<string, string>
  customDimensionGroupColorById?: Record<string, string>
  onHoveredRunIdChange: (runId: string | null) => void
  onMetricViewsChange?: (metricViews: MetricViews) => void
  onRowClick: (simulationOutputId: string) => void
  onSelectedMetricIdChange: (metricId: string | null) => void
  onSelectedSimulationOutputIdsChange: (ids: string[]) => void
  personasById: PersonasById
  runRows: ReportRunRows
  runsById: ReportRunsById
  secondaryCompareBy?: CompareBy | null
  selectedMetricId: string | null
  selectedSimulationOutputIds: string[]
  simOutputToRunId: Map<string, string>
  splitSizes: number[]
  viewMode: ReportViewMode
  metricLayout?: MetricLayoutConfig
  onMetricLayoutChange?: (layout: MetricLayoutConfig) => void
  editLayout?: boolean
  /** Which panes the report shows. Absent = both. */
  paneVisibility?: ReportPaneVisibility
  /** When provided alongside editLayout, each pane gets a show/hide checkbox. */
  onPaneVisibilityChange?: (visibility: ReportPaneVisibility) => void
  selectedPlotId?: string | null
  onSelectedPlotIdChange?: (plotId: string | null) => void
  setSplitSizes: (sizes: number[]) => void
  showRightPaneLoadingSkeleton?: boolean
}

export function ReportWorkspace({
  agentsById,
  compareByColors,
  compareByConfigForGroup,
  compareByDisplayLabels,
  hoveredRunId = null,
  isLoading,
  isShared = false,
  leftPaneHeader,
  leftPaneOverride,
  metricRecordsById,
  rerunMetricRecordsById = null,
  metricViews,
  forceSelectMode = false,
  selectionToolbarLeading,
  customDimensionGroupBySimId,
  customDimensionGroupNameById,
  customDimensionGroupColorById,
  onHoveredRunIdChange,
  onMetricViewsChange,
  onRowClick,
  onSelectedMetricIdChange,
  onSelectedSimulationOutputIdsChange,
  personasById,
  runRows,
  runsById,
  secondaryCompareBy = null,
  selectedMetricId,
  selectedSimulationOutputIds,
  simOutputToRunId,
  splitSizes,
  viewMode,
  metricLayout,
  onMetricLayoutChange,
  editLayout = false,
  paneVisibility = DEFAULT_REPORT_PANE_VISIBILITY,
  onPaneVisibilityChange,
  selectedPlotId = null,
  onSelectedPlotIdChange,
  setSplitSizes,
  showRightPaneLoadingSkeleton = false,
}: ReportWorkspaceProps) {
  const compareByConfigWithColors = useMemo(
    () =>
      compareByConfigForGroup
        ? {
            ...compareByConfigForGroup,
            colors: compareByColors ?? {},
          }
        : null,
    [compareByColors, compareByConfigForGroup],
  )

  const compareByWithLabels = useMemo(
    () =>
      compareByConfigWithColors
        ? {
            ...compareByConfigWithColors,
            displayLabels: compareByDisplayLabels,
          }
        : null,
    [compareByConfigWithColors, compareByDisplayLabels],
  )
  const metricFilterAdaptersById = useMemo(
    () => buildMetricFilterAdapters(metricRecordsById),
    [metricRecordsById],
  )
  const metricRenderAdaptersById = useMemo(
    () => buildMetricRenderAdapters(metricRecordsById),
    [metricRecordsById],
  )
  const metricSourcesById = useMemo(
    () => buildMetricAutoAddRuleSources(metricRecordsById),
    [metricRecordsById],
  )

  // Plot blocks live inside the metric layout; mutate them through the same
  // onMetricLayoutChange path (a plot only exists after it was added, which
  // materializes the layout, so the saved layout always contains it here).
  const canEditPlots = !isShared && Boolean(onMetricLayoutChange)
  const updatePlot = useCallback(
    (next: ComparisonPlotConfig) => {
      if (!metricLayout || !onMetricLayoutChange) {
        return
      }
      onMetricLayoutChange({
        sections: updatePlotInLayout(metricLayout.sections, next),
      })
    },
    [metricLayout, onMetricLayoutChange],
  )
  const removePlot = useCallback(
    (plotId: string) => {
      if (!metricLayout || !onMetricLayoutChange) {
        return
      }
      onMetricLayoutChange({
        sections: removePlotFromLayout(metricLayout.sections, plotId),
      })
      if (selectedPlotId === plotId) {
        onSelectedPlotIdChange?.(null)
      }
    },
    [
      metricLayout,
      onMetricLayoutChange,
      selectedPlotId,
      onSelectedPlotIdChange,
    ],
  )

  const selectedPlot = useMemo<ComparisonPlotConfig | null>(() => {
    if (!selectedPlotId || !metricLayout) {
      return null
    }
    for (const section of metricLayout.sections) {
      for (const item of section.items) {
        if (item.kind === "plot" && item.plot.id === selectedPlotId) {
          return item.plot
        }
      }
    }
    return null
  }, [selectedPlotId, metricLayout])

  // Clear a stale plot selection if its plot disappears (e.g. its section was
  // removed) so the right pane falls back to the table.
  useEffect(() => {
    if (selectedPlotId && !selectedPlot) {
      onSelectedPlotIdChange?.(null)
    }
  }, [selectedPlotId, selectedPlot, onSelectedPlotIdChange])

  // Stable identities so unrelated workspace re-renders (e.g. row hover) don't
  // churn the metric/plot section tree.
  const renderPlotBlock = useCallback(
    (plot: ComparisonPlotConfig) => (
      <PlotCard
        config={plot}
        runRows={runRows}
        compareBy={compareByWithLabels}
        metricRecordsById={metricRecordsById}
        onPointClick={onRowClick}
        readOnly={isShared}
        selected={plot.id === selectedPlotId}
        showRemove={editLayout && !isShared}
        onSelect={() => onSelectedPlotIdChange?.(plot.id)}
        onTitleChange={(title) =>
          updatePlot({ ...plot, title: title || undefined })
        }
        onRemove={() => removePlot(plot.id)}
      />
    ),
    [
      runRows,
      compareByWithLabels,
      metricRecordsById,
      onRowClick,
      isShared,
      selectedPlotId,
      editLayout,
      onSelectedPlotIdChange,
      updatePlot,
      removePlot,
    ],
  )
  const createPlotFn = useCallback(
    () => COMPARISON_PLOT_ENTRIES[0].defaultConfig(),
    [],
  )
  const createPlot = canEditPlots ? createPlotFn : undefined

  const paneToggleIdPrefix = useId()
  const showPaneToggles = editLayout && Boolean(onPaneVisibilityChange)
  // The custom-dimension panel takes the left pane over entirely, so it shows
  // even when the report hides its metrics.
  const showMetricsContent = Boolean(leftPaneOverride) || paneVisibility.metrics
  // A selected plot puts its options editor in the right pane, which is an
  // editing surface rather than the report's table.
  const showTableContent = paneVisibility.table || Boolean(selectedPlot)
  // In edit mode both panes stay mounted so a hidden pane's checkbox is still
  // reachable; outside it a hidden pane drops out and the other fills the row.
  const renderMetricsPane = showPaneToggles || showMetricsContent
  const renderTablePane = showPaneToggles || showTableContent
  const isSplit = renderMetricsPane && renderTablePane

  const metricsPane = (
    <div className="min-w-0 overflow-y-auto flex flex-col min-h-0 bg-surface-contrast">
      {showPaneToggles && (
        <PaneVisibilityToggle
          checked={paneVisibility.metrics}
          id={`${paneToggleIdPrefix}-metrics`}
          isOnlyVisiblePane={!paneVisibility.table}
          label="Show metrics"
          onCheckedChange={(checked) =>
            onPaneVisibilityChange?.({ ...paneVisibility, metrics: checked })
          }
        />
      )}
      {!showMetricsContent ? (
        <HiddenPaneNote />
      ) : leftPaneOverride ? (
        leftPaneOverride
      ) : (
        <>
          <ScopedSimulationsBanner
            runRows={runRows}
            selectedSimulationOutputIds={selectedSimulationOutputIds}
            className="mx-4 mt-4 shrink-0"
          />
          {leftPaneHeader}
          <RunMetricCards
            runRows={runRows}
            metricRenderAdaptersById={metricRenderAdaptersById}
            isLoading={isLoading}
            selectedSimulationOutputIds={selectedSimulationOutputIds}
            selectedMetricId={selectedMetricId}
            onSelectedMetricIdChange={onSelectedMetricIdChange}
            metricViews={metricViews}
            onMetricViewsChange={onMetricViewsChange}
            compareBy={compareByWithLabels}
            rerunMetricRecordsById={rerunMetricRecordsById}
            metricLayout={metricLayout}
            onMetricLayoutChange={onMetricLayoutChange}
            editLayout={editLayout}
            renderPlotBlock={renderPlotBlock}
            createPlot={createPlot}
            onPlotAdded={onSelectedPlotIdChange}
          />
        </>
      )}
    </div>
  )

  // The table rides on a card, inset from the shell edges the way every other
  // table view is. The inset lives on this pane rather than the card because
  // react-split writes the pane's width inline, which a width utility could not
  // survive. Its checkbox sits inside the card so the frame opens at the
  // control rather than between it and the rows.
  const tablePane = (
    <div
      role="presentation"
      className={cn(
        "min-h-0 min-w-0 bg-surface-contrast pr-6 pb-6",
        !isSplit && "pl-6",
      )}
      onMouseMove={(event) => {
        const target = event.target as HTMLElement
        const row = target.closest("tr")
        const simulationOutputId = row?.getAttribute("data-key")

        if (!simulationOutputId) {
          return
        }

        const runId = simOutputToRunId.get(simulationOutputId)
        if (runId && runId !== hoveredRunId) {
          onHoveredRunIdChange(runId)
        }
      }}
      onMouseLeave={() => onHoveredRunIdChange(null)}
    >
      <div className={cn("flex h-full w-full flex-col", TABLE_FRAME_CLASS)}>
        {showPaneToggles && (
          <PaneVisibilityToggle
            checked={paneVisibility.table}
            id={`${paneToggleIdPrefix}-table`}
            isOnlyVisiblePane={!paneVisibility.metrics}
            label="Show results table"
            onCheckedChange={(checked) =>
              onPaneVisibilityChange?.({ ...paneVisibility, table: checked })
            }
          />
        )}
        {/* The card clips; each results table scrolls its own body, so this
            region only has to give them a bounded height. */}
        <div className="min-h-0 min-w-0 flex-1">
          {selectedPlot ? (
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-coval-border px-4 py-2">
                <span className="text-sm font-medium text-coval-text">
                  Plot options
                </span>
                <button
                  type="button"
                  aria-label="Close plot options"
                  onClick={() => onSelectedPlotIdChange?.(null)}
                  className="p-1 text-coval-text-muted hover:text-coval-text"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <PlotOptionsPanel
                  config={selectedPlot}
                  metricRecordsById={metricRecordsById}
                  compareBy={compareByWithLabels}
                  onChange={updatePlot}
                />
              </div>
            </div>
          ) : !showTableContent ? (
            <HiddenPaneNote />
          ) : showRightPaneLoadingSkeleton && isLoading ? (
            <TableSkeleton />
          ) : compareByWithLabels === null ||
            viewMode === REPORT_VIEW_MODE.ROWS ? (
            <RunResultsTable
              key={`compare-${compareByConfigWithColors?.dimension ?? "none"}-${compareByConfigWithColors?.metadataKey ?? ""}-${Object.values(compareByColors ?? {}).join(",")}`}
              runRows={runRows}
              runRecord={null}
              metricFilterAdaptersById={metricFilterAdaptersById}
              metricRenderAdaptersById={metricRenderAdaptersById}
              metricSourcesById={metricSourcesById}
              onRowClick={onRowClick}
              onSelectionChange={onSelectedSimulationOutputIdsChange}
              isLoading={isLoading}
              metricIdFilter={selectedMetricId}
              compareBy={compareByWithLabels}
              agentsById={agentsById}
              personasById={personasById}
              isShared={isShared}
              metricViews={metricViews}
              rerunMetricRecordsById={rerunMetricRecordsById}
            />
          ) : (
            <GroupedResultsTable
              key={`compare-${compareByConfigWithColors?.dimension ?? "none"}-${compareByConfigWithColors?.metadataKey ?? ""}`}
              runRows={runRows}
              metricRenderAdaptersById={metricRenderAdaptersById}
              onRowClick={onRowClick}
              metricIdFilter={selectedMetricId}
              agentsById={agentsById}
              personasById={personasById}
              runsById={runsById}
              compareBy={compareByWithLabels}
              secondaryCompareBy={secondaryCompareBy}
              metricViews={metricViews}
              selectedSimulationOutputIds={selectedSimulationOutputIds}
              onSelectedSimulationOutputIdsChange={
                onSelectedSimulationOutputIdsChange
              }
              rerunMetricRecordsById={rerunMetricRecordsById}
              forceSelectMode={forceSelectMode}
              selectionToolbarLeading={selectionToolbarLeading}
              customDimensionGroupBySimId={customDimensionGroupBySimId}
              customDimensionGroupNameById={customDimensionGroupNameById}
              customDimensionGroupColorById={customDimensionGroupColorById}
            />
          )}
        </div>
      </div>
    </div>
  )

  if (!isSplit) {
    return (
      <div className="flex-1 flex min-h-0 [&>*]:flex-1">
        {renderMetricsPane ? metricsPane : tablePane}
      </div>
    )
  }

  return (
    <Split
      className="flex-1 flex min-h-0"
      sizes={splitSizes}
      minSize={300}
      gutterSize={0}
      gutterAlign="center"
      gutter={() => {
        const gutter = document.createElement("div")
        gutter.className = "run-page-gutter gutter gutter-horizontal group"
        gutter.appendChild(createResizeGrip())
        return gutter
      }}
      onDragEnd={setSplitSizes}
    >
      {metricsPane}
      {tablePane}
    </Split>
  )
}

/** Edit-layout control that shows or hides one side of the workspace. The
 *  last visible pane can't be hidden, so the report always shows something. */
function PaneVisibilityToggle({
  checked,
  id,
  isOnlyVisiblePane,
  label,
  onCheckedChange,
}: {
  checked: boolean
  id: string
  isOnlyVisiblePane: boolean
  label: string
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex shrink-0 items-center gap-2 px-4 py-2">
      <Checkbox
        id={id}
        checked={checked}
        disabled={checked && isOnlyVisiblePane}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <Label
        htmlFor={id}
        className="cursor-pointer text-sm"
        title={
          checked && isOnlyVisiblePane
            ? "At least one side has to stay visible"
            : undefined
        }
      >
        {label}
      </Label>
    </div>
  )
}

function HiddenPaneNote() {
  return (
    <div className="p-4 text-sm text-coval-text-muted">
      Hidden from this report
    </div>
  )
}
